import {
  SOLVE_CANVAS_WIDGET_KINDS,
  type InstallableModuleKey,
  type WidgetKind,
  type SolvePhase,
} from "@playbookai/types";

/**
 * Widget kinds users and Player AI may add to a SOLVE phase.
 *
 * These 22 names are DELIBERATELY written out again here rather than derived
 * from `SOLVE_CANVAS_WIDGET_KINDS` (PB-146 review). Three lists overlap:
 *
 *  - this one — what the "+ Widget" popover offers, grouped by phase;
 *  - `SOLVE_AI_WIDGET_KINDS` (`@playbookai/widget-contracts`) — what Player AI
 *    may create, which it gates itself because its tools bypass the placement
 *    rail;
 *  - `SOLVE_CANVAS_WIDGET_KINDS` (`@playbookai/types`) — what the canvas may
 *    HOST, which is deliberately wider: it also carries the two legacy Oppress
 *    kinds that existing Solves hold but nothing offers.
 *
 * They answer different questions, so collapsing them would either invert which
 * list is authoritative or lose the phase grouping below. Drift is caught
 * instead: `solve-widget-catalog.spec.ts` pins this list as a subset of the
 * roster, and `surface-widget-kinds.spec.ts` pins the AI list against it too.
 */
export const SOLVE_WIDGET_KINDS: readonly WidgetKind[] = [
  "problemStatement",
  "tags",
  "runChart",
  "kpiTile",
  "problemTree",
  "problemTimeline",
  "containmentPlan",
  "exposureRiskReview",
  "paretoL1",
  "paretoL2",
  "funnel",
  "causalChain",
  "fishbone",
  "processTrace",
  "impactEffort",
  "countermeasureTable",
  "stakeholderMap",
  "controlPlan",
  "escalationMatrix",
  // Phase-agnostic: the caller chooses the active phase at placement time.
  "genericTable",
  "markdownText",
];

/**
 * The SOLVE widgets that show another module's data, and that module
 * (ADR-0100). "+ Widget" leaves a kind out while its module is off. Only the
 * KPI Tile reads another module: the Run Chart plots its own points (m1).
 */
export const SOLVE_WIDGET_MODULE: Readonly<
  Partial<Record<WidgetKind, InstallableModuleKey>>
> = {
  kpiTile: "kpi",
};

/**
 * Kinds the canvas can render. The two legacy Oppress kinds stay renderable
 * for existing Solves but are intentionally absent from defaults and Add.
 *
 * Re-exported from `@playbookai/types` rather than declared here: the placement
 * rail and the collab projection enforce the same roster server-side, so it has
 * to be one shared list (PB-146). `solve-widget-catalog.spec.ts` pins
 * `SOLVE_WIDGET_KINDS` as a subset of it.
 */
export const SOLVE_RENDERABLE_WIDGET_KINDS: readonly WidgetKind[] =
  SOLVE_CANVAS_WIDGET_KINDS;

/**
 * SOLVE-specific overlay: which canonical phase each widget seeds into.
 *
 * `WidgetDef` itself is module-agnostic. This map is the SOLVE-side overlay
 * used by `seed-widgets.ts` to place widgets in the right swimlane on
 * first canvas open. Mirrors the reference `widgetCatalog.ts`.
 *
 * Widgets with `defaultRoster: false` (kpiTile, stakeholderMap) are NOT
 * seeded automatically even if they have an entry here — they appear from
 * the "+ Widget" popover only.
 */
export const SOLVE_DEFAULT_PHASE: Partial<Record<WidgetKind, SolvePhase>> = {
  // Specify
  problemStatement: "Specify",
  tags: "Specify",
  runChart: "Specify",
  kpiTile: "Specify",
  problemTree: "Specify",
  // Oppress
  problemTimeline: "Oppress",
  containmentPlan: "Oppress",
  exposureRiskReview: "Oppress",
  // Look
  paretoL1: "Look",
  paretoL2: "Look",
  funnel: "Look",
  causalChain: "Look",
  fishbone: "Look",
  processTrace: "Look",
  // Verify
  impactEffort: "Verify",
  countermeasureTable: "Verify",
  stakeholderMap: "Verify",
  // Ensure
  controlPlan: "Ensure",
  escalationMatrix: "Ensure",
};
